import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Accordion } from '../../components/Accordion';
import { Badge } from '../../components/Badge';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { SkeletonText } from '../../components/Skeleton';
import { formatDateTime } from '../../i18n';
import { formatSize, useFileDownload } from '../tickets/articles/attachmentFile';
import { downloadArticleAttachment } from './api';
import { RichText } from '../../components/RichText';
import { sectionAnchor } from './html';
import { useArticleVersion, useArticleVersions, useVersionAttachments } from './queries';
import { readableSections } from './sections';

interface ArticleVersionsProps {
  articleId: string;
  /** Die Nummer der geltenden Fassung. Woran sonst sollte sich zeigen,
   *  welche der Fassungen die historischen sind. */
  currentVersion: number | null;
}

/**
 * Die Fassungen eines Artikels -- F4-12, F4-24, F4-34.
 *
 * **Als Abfolge und nicht als Definitionsliste.** Vorher standen Nummer und
 * Datum in zwei Spalten nebeneinander; dass die eine aus der anderen
 * hervorgegangen ist, sah man nicht. Fassungen sind aber genau das -- eine
 * Kette. Die Linie zwischen den Punkten sagt es, ohne ein Wort zu kosten.
 *
 * Die neueste steht oben und traegt den gefuellten Punkt: Wer nachsieht,
 * fragt fast immer nach der letzten Aenderung.
 *
 * **Und seit F4-34 laesst sich eine Fassung oeffnen.** Bis dahin stand hier
 * nur, *dass* an einem Tag etwas geaendert wurde -- was dort stand, lag in
 * der Datenbank und nirgends sonst. Das Versionsmodell verspricht den
 * Nachweis "was stand zum Zeitpunkt X"; ohne diesen Weg war das Versprechen
 * nicht einloesbar. Beim importierten Altbestand ist es die einzige Stelle,
 * an der die Originalfassung (v1) ueberhaupt zu sehen ist.
 *
 * **Aufklappen und kein eigenes Fenster.** Der Vergleich zweier Faelle ist
 * der Grund, aus dem jemand hier ist -- "was stand vorher da?" --, und ein
 * Fenster ueber der Liste verdeckte genau die Liste. Aufgeklappt bleibt der
 * Zusammenhang stehen: die Fassung, ihr Vermerk und die daneben.
 */
export function ArticleVersions({ articleId, currentVersion }: ArticleVersionsProps) {
  const { t, i18n } = useTranslation();
  const versions = useArticleVersions(articleId);
  const [open, setOpen] = useState<string | null>(null);

  if (versions.isError) return <ErrorNotice error={versions.error} />;

  const fassungen = [...(versions.data ?? [])].sort((a, b) => b.version - a.version);

  if (fassungen.length === 0) return <p className="table__muted">{t('knowledge.noVersions')}</p>;

  return (
    <ul className="kb-versions">
      {fassungen.map((version) => {
        const offen = open === version.id;
        const gilt = version.version === currentVersion;
        const panelId = `kb-version-${version.id}`;

        return (
          <li key={version.id} className="kb-version">
            <span className="kb-version__dot" aria-hidden="true" />
            <div className="kb-version__body">
              {/* Die ganze Zeile ist der Schalter und nicht ein Knopf am
                  Rand: Das Ziel ist groesser, und es gibt nichts daneben,
                  was man stattdessen treffen koennte. */}
              <button
                type="button"
                className="kb-version__toggle"
                aria-expanded={offen}
                aria-controls={panelId}
                onClick={() => {
                  setOpen(offen ? null : version.id);
                }}
              >
                <span className="kb-version__number">
                  {t('knowledge.version', { number: version.version })}
                </span>
                {gilt ? <Badge tone="success">{t('knowledge.versionCurrent')}</Badge> : null}
                {version.change_note ? (
                  <span className="kb-version__note">{version.change_note}</span>
                ) : null}
                <span className="kb-version__moment">
                  {formatDateTime(version.created_at, i18n.language)}
                </span>
                <span className="kb-version__action">
                  {offen ? t('knowledge.versionHide') : t('knowledge.versionShow')}
                </span>
              </button>

              {/* Der Bereich, auf den ``aria-controls`` zeigt, muss es geben
                  -- sonst zeigt die Angabe ins Leere. Geladen wird sein
                  Inhalt aber erst, wenn er offen ist: Bei einem Artikel mit
                  zwanzig Fassungen waeren das sonst zwanzig Abrufe fuer eine
                  Frage, die sich auf eine bezieht. */}
              <div id={panelId} className="kb-version__panel" hidden={!offen}>
                {offen ? (
                  <VersionDetail
                    articleId={articleId}
                    versionId={version.id}
                    historic={!gilt}
                    createdAt={version.created_at}
                  />
                ) : null}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Was in einer Fassung stand -- Text, Gliederung und die Dateien von damals.
 *
 * Zwei Abrufe und nicht einer: Der Inhalt kommt vom Fassungsendpunkt, die
 * Dateien von dem, der auch die Dateien der geltenden Fassung ausgibt (mit
 * ``version_id``). Die Regeln fuer das Herausgeben einer Datei stehen damit
 * weiterhin an genau einer Stelle.
 */
function VersionDetail({
  articleId,
  versionId,
  historic,
  createdAt,
}: {
  articleId: string;
  versionId: string;
  historic: boolean;
  createdAt: string;
}) {
  const { t, i18n } = useTranslation();
  const version = useArticleVersion(articleId, versionId);
  const attachments = useVersionAttachments(articleId, versionId);
  const fetchFile = useFileDownload();

  if (version.isPending) return <SkeletonText lines={4} />;
  if (version.isError) return <ErrorNotice error={version.error} />;

  const sichtbar = readableSections(
    version.data.sections,
    version.data.body_html,
    t('knowledge.legacySection'),
  );
  const dateien = attachments.data ?? [];

  return (
    <div className="kb-version__content">
      {/* Der Hinweis steht oben und nicht unten: Wer eine Anleitung liest und
          erst danach erfaehrt, dass sie ueberholt ist, hat sie schon
          befolgt. */}
      {historic ? (
        <p className="kb-version__banner" role="note">
          <strong>
            {t('knowledge.versionHistoric', {
              moment: formatDateTime(createdAt, i18n.language),
            })}
          </strong>{' '}
          {t('knowledge.versionHistoricHint')}
        </p>
      ) : null}

      {sichtbar.length === 0 ? (
        <EmptyState icon="knowledge" title={t('knowledge.noText')} />
      ) : (
        // Dieselbe Darstellung wie in der Leseansicht, und dieselben
        // Rueckfaelle: Eine Fassung aus dem Altbestand traegt keine
        // Gliederung, sondern nur ihren Text -- ``readableSections`` macht
        // daraus einen Abschnitt. Ohne den blieben zwei Drittel des
        // importierten Bestands hier leer.
        //
        // Die Sprungmarken tragen die Fassungskennung: Zwei offene Fassungen
        // haetten sonst dieselben Kennungen im Dokument, und ein Verweis
        // fuehrte in die falsche.
        <Accordion
          openInitially={sichtbar.length}
          items={sichtbar.map((section, index) => ({
            id: `${versionId}-${String(index)}`,
            anchor: `${sectionAnchor(index)}-${versionId}`,
            heading: section.heading,
            content: (
              // Entschaerft wurde beim Speichern, auf dem Server -- siehe
              // ``ArticleBody``. Fuer eine alte Fassung gilt dasselbe: Sie
              // ist durch dieselbe Pruefung gelaufen, als sie geschrieben
              // wurde, und laeuft beim Anzeigen noch einmal durch die
              // heutige.
              <RichText html={section.body_html} />
            ),
          }))}
        />
      )}

      {dateien.length > 0 ? (
        <div className="kb-version__files">
          <h4 className="kb-version__files-title">{t('knowledge.versionAttachments')}</h4>
          {fetchFile.isError ? <ErrorNotice error={fetchFile.error} /> : null}
          {dateien.map((attachment) => (
            <div key={attachment.id} className="kb-file">
              <span className="kb-file__name">
                <button
                  type="button"
                  className="table__subject"
                  onClick={() => {
                    fetchFile.mutate({
                      fetchFile: () => downloadArticleAttachment(articleId, attachment.id),
                      fallbackName: attachment.filename,
                    });
                  }}
                >
                  {attachment.filename}
                </button>
              </span>
              <span className="kb-file__size">{formatSize(attachment.size_bytes)}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
